import React, { useMemo, useState } from 'react';
import { Search as SearchIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { products, categories, waLink, waLinkProps } from '../mock';
import WhatsAppIcon from './WhatsAppIcon';
import { trackWhatsAppClick, trackEvent } from '../lib/analytics';

const SearchOverlay = ({ open, onClose, onOpenProduct }) => {
  const [query, setQuery] = useState('');

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return products.filter((p) => {
      const fields = [p.name, p.description, p.material, ...(p.variants || []).map((v) => v.name)];
      return fields.some((field) => field && field.toLowerCase().includes(q));
    });
  }, [query]);

  const handleSelect = (product) => {
    onOpenProduct(product);
    onClose();
  };

  const handleOpenChange = (v) => {
    if (!v) {
      // Si se cierra el buscador sin resultados, guarda qué buscaron — es información
      // valiosa de "qué productos faltan" para el reporte mensual.
      if (query.trim() && results.length === 0) {
        trackEvent('search_no_results', { query: query.trim() });
      }
      setQuery('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="q-search" data-testid="search-overlay" aria-describedby={undefined}>
        <DialogTitle className="sr-only">Buscar productos</DialogTitle>
        <div className="q-search__input-row">
          <SearchIcon size={18} strokeWidth={1.5} className="q-search__icon" />
          <input
            type="text"
            autoFocus
            placeholder="Buscar en gorras, lentes y joyería…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="q-search__input"
            data-testid="search-input"
          />
        </div>

        <div className="q-search__results">
          {query.trim() === '' && (
            <p className="q-search__hint">Escribe para buscar en todo el catálogo.</p>
          )}

          {query.trim() !== '' && results.length === 0 && (
            <div className="q-search__empty" data-testid="search-empty-state">
              <p className="q-search__empty-title">No encontramos nada con «{query}»</p>
              <p className="q-search__empty-text">Intenta con otra palabra o pregunta directo por WhatsApp.</p>
              <a
                href={waLink(`Hola QUINLATE, busco: ${query}. ¿Lo tienen disponible?`)}
                {...waLinkProps}
                className="q-btn q-btn--ghost q-btn--sm q-btn--wa"
                data-testid="search-empty-whatsapp"
                onClick={() => trackWhatsAppClick('search_empty', { query })}
              >
                <WhatsAppIcon size={16} /> Preguntar por WhatsApp
              </a>
            </div>
          )}

          {results.map((p) => (
            <button
              key={p.id}
              type="button"
              className="q-search__result"
              onClick={() => handleSelect(p)}
              data-testid={`search-result-${p.id}`}
            >
              <img src={p.image} alt={p.name} className="q-search__result-img" />
              <span className="q-search__result-info">
                <span className="q-search__result-name">{p.name}</span>
                <span className="q-search__result-meta">
                  {categories[p.category]?.title || p.category} · {p.price}
                </span>
              </span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchOverlay;
